import React, { useState, useEffect, useCallback } from "react";
import {
  FormControl,
  Select,
  MenuItem,
  Box,
  Button,
  SelectChangeEvent,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

interface DateFilterOption {
  readonly value: string;
  readonly label: string;
}

interface DateFilterProps {
  selectedValue: string;
  options: ReadonlyArray<DateFilterOption>;
  onValueChange: (newInternalValue: string) => void;
}

const CUSTOM_RANGE_ID = "__custom_range__";
const CUSTOM_RANGE_LABEL = "Select Period";

const normalizeToStartOfDay = (d: Date): Date => {
  const dt = new Date(d);
  dt.setHours(0, 0, 0, 0);
  return dt;
};

const normalizeToEndOfDay = (d: Date): Date => {
  const dt = new Date(d);
  dt.setHours(23, 59, 59, 999);
  return dt;
};

const formatTimestampsForDisplay = (timestampRange: string): string => {
  if (!timestampRange.includes("-")) return "Invalid Range";
  const parts = timestampRange.split("-");
  if (parts.length !== 2 || !parts[0] || !parts[1])
    return "Invalid Range Format";

  const startMs = Number(parts[0]) * 1000;
  const endMs = Number(parts[1]) * 1000;

  const start = new Date(startMs);
  const end = new Date(endMs);

  const fmt = (d: Date) =>
    d.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });

  return `${fmt(start)} - ${fmt(end)}`;
};

const DateFilter: React.FC<DateFilterProps> = ({
  selectedValue,
  options,
  onValueChange,
}) => {
  const [showDatePickers, setShowDatePickers] = useState(false);
  const [tempStartDate, setTempStartDate] = useState<Date | null>(null);
  const [tempEndDate, setTempEndDate] = useState<Date | null>(null);

  const [activeKeyForSelect, setActiveKeyForSelect] = useState<string>(() => {
    if (options.some((o) => o.value === selectedValue)) return selectedValue;
    if (selectedValue.includes("-")) return CUSTOM_RANGE_ID;
    return options[0]?.value ?? "";
  });

  useEffect(() => {
    if (options.some((o) => o.value === selectedValue)) {
      setActiveKeyForSelect(selectedValue);
      setShowDatePickers(false);
    } else if (selectedValue.includes("-")) {
      setActiveKeyForSelect(CUSTOM_RANGE_ID);
      const [s, e] = selectedValue.split("-").map((str) => Number(str) * 1000);
      setTempStartDate(new Date(s));
      setTempEndDate(new Date(e));
    } else {
      setActiveKeyForSelect(options[0]?.value ?? "");
      setShowDatePickers(false);
    }
  }, [selectedValue, options]);

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const val = e.target.value;
    setActiveKeyForSelect(val);
    if (val !== CUSTOM_RANGE_ID) {
      setShowDatePickers(false);
      onValueChange(val);
    }
  };

  const handleApplyCustomRange = () => {
    if (!tempStartDate || !tempEndDate) return;
    if (tempEndDate < tempStartDate) {
      alert("End date cannot be earlier than start date.");
      return;
    }
    const from = normalizeToStartOfDay(tempStartDate);
    const to = normalizeToEndOfDay(tempEndDate);
    const fromSec = Math.floor(from.getTime() / 1000);
    const toSec = Math.floor(to.getTime() / 1000);
    onValueChange(`${fromSec}-${toSec}`);
    setShowDatePickers(false);
  };

  const handleCancelCustomRange = () => {
    setShowDatePickers(false);
    if (options.some((o) => o.value === selectedValue)) {
      setActiveKeyForSelect(selectedValue);
    } else if (selectedValue.includes("-")) {
      setActiveKeyForSelect(CUSTOM_RANGE_ID);
    } else {
      setActiveKeyForSelect(options[0]?.value ?? "");
    }
  };

  const renderSelectValue = useCallback(
    (key: string) =>
      key === CUSTOM_RANGE_ID && selectedValue.includes("-")
        ? formatTimestampsForDisplay(selectedValue)
        : (options.find((o) => o.value === key)?.label ?? ""),
    [options, selectedValue],
  );

  return (
    <Box sx={{ position: "relative" }}>
      <FormControl variant="outlined" size="small" sx={{ minWidth: 180 }}>
        <Select
          value={activeKeyForSelect}
          onChange={handleSelectChange}
          displayEmpty
          renderValue={renderSelectValue}
          sx={{
            color: "primary.main",
            ".MuiSelect-icon": { color: "primary.main" },
            "& .MuiSelect-select:focus": { backgroundColor: "transparent" },
            "& .MuiOutlinedInput-root fieldset": {
              borderColor: "primary.main",
            },
            "& .MuiOutlinedInput-root:hover fieldset": {
              borderColor: "primary.main",
            },
            "& .MuiOutlinedInput-root.Mui-focused fieldset": {
              borderColor: "primary.main",
            },
            borderRadius: 1,
          }}
        >
          {options.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}

          <MenuItem
            key={CUSTOM_RANGE_ID}
            value={CUSTOM_RANGE_ID}
            onClick={() => {
              setActiveKeyForSelect(CUSTOM_RANGE_ID);
              setShowDatePickers(true);
              if (selectedValue.includes("-")) {
                const [s, e] = selectedValue
                  .split("-")
                  .map((str) => Number(str) * 1000);
                setTempStartDate(new Date(s));
                setTempEndDate(new Date(e));
              } else {
                setTempStartDate(new Date());
                setTempEndDate(new Date());
              }
            }}
          >
            {CUSTOM_RANGE_LABEL}
          </MenuItem>
        </Select>
      </FormControl>

      {showDatePickers && (
        <Box
          sx={{
            mt: 1,
            p: 2,
            border: "1px solid",
            borderColor: "calibrate.primaryBorder30",
            borderRadius: 1,
            backgroundColor: "white",
            position: "absolute",
            zIndex: 1301,
            boxShadow: 3,
            minWidth: 280,
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="From"
              value={tempStartDate}
              onChange={setTempStartDate}
              slotProps={{
                textField: { size: "small", fullWidth: true, sx: { mb: 2 } },
                popper: {
                  placement: "top-start",
                  modifiers: [{ name: "offset", options: { offset: [0, 8] } }],
                  sx: { zIndex: 1400 },
                },
              }}
            />
            <DatePicker
              label="To"
              value={tempEndDate}
              onChange={setTempEndDate}
              minDate={tempStartDate ?? undefined}
              slotProps={{
                textField: { size: "small", fullWidth: true },
                popper: {
                  placement: "top-start",
                  modifiers: [{ name: "offset", options: { offset: [0, 8] } }],
                  sx: { zIndex: 1400 },
                },
              }}
            />
          </LocalizationProvider>

          <Box
            sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}
          >
            <Button
              onClick={handleCancelCustomRange}
              size="small"
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleApplyCustomRange}
              variant="contained"
              size="small"
              disabled={
                !tempStartDate ||
                !tempEndDate ||
                tempEndDate < (tempStartDate as Date)
              }
            >
              Apply
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DateFilter;
